/**
 * Black-Scholes option pricing model implementation
 * Ported from Python backend (contracts/utils/options_analysis.py)
 */

/**
 * Intraday near-expiry floor for time-to-expiry, in YEARS (~5 minutes). Used
 * only by intraday repricing so a 30-minute hold near the close isn't clamped to
 * the standard ~53-minute (0.0001 yr) floor. Below this, Black-Scholes already
 * converges to intrinsic value via its NaN/Inf guards.
 */
export const INTRADAY_T_FLOOR_YEARS = 5 / (365 * 24 * 60);

/**
 * Return the ET (America/New_York) UTC offset, in minutes, for a given UTC
 * instant — DST-correct via Intl (e.g. -240 during EDT, -300 during EST).
 */
function etOffsetMinutes(utcMs) {
  const p = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(utcMs)).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
  return (Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second) - utcMs) / 60000;
}

/**
 * Fractional days from `now` until the contract expires at 16:00 ET (market
 * close) on its expiration date. Generalizes the integer "Days Until Expiration"
 * to sub-day resolution so intraday (incl. 0DTE) can decay against real
 * remaining time. May be ≤ 0 if already past the close.
 * @param {string} expirationStr - 'YYYY-MM-DD'
 * @param {Date} now
 * @returns {number|null} fractional days, or null if the date is unparseable
 */
export function getFractionalDaysToExpiry(expirationStr, now = new Date()) {
  if (!expirationStr) return null;
  const [y, m, d] = String(expirationStr).split('-').map(Number);
  if (!y || !m || !d) return null;
  let utc = Date.UTC(y, m - 1, d, 16, 0, 0);
  utc -= etOffsetMinutes(utc) * 60000; // shift so the ET wall clock reads 16:00
  return (utc - now.getTime()) / 86400000;
}

/**
 * Normal cumulative distribution function
 * @param {number} x - Input value
 * @returns {number} CDF value
 */
function normCDF(x) {
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;

  // Save the sign of x
  const sign = x >= 0 ? 1 : -1;
  x = Math.abs(x) / Math.sqrt(2);

  // A&S formula 7.1.26
  const t = 1.0 / (1.0 + p * x);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

  return 0.5 * (1.0 + sign * y);
}

/**
 * Black-Scholes call option price calculation
 * @param {number} S - Current stock price
 * @param {number} K - Strike price
 * @param {number} T - Time to expiration (in years)
 * @param {number} r - Risk-free rate (as decimal, e.g., 0.05 for 5%)
 * @param {number} sigma - Volatility (as decimal, e.g., 0.3 for 30%)
 * @returns {number} Call option price
 */
export function blackScholesCall(S, K, T, r, sigma) {
  // Input validation to prevent mathematical errors
  if (T <= 0 || sigma <= 0 || S <= 0 || K <= 0) {
    return Math.max(S - K, 0); // Return intrinsic value
  }

  // Prevent extreme values that cause numerical issues
  if (S / K > 1000 || K / S > 1000) {
    // For extremely out-of-the-money options, return intrinsic value
    return Math.max(S - K, 0);
  }

  try {
    const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T));
    const d2 = d1 - sigma * Math.sqrt(T);

    // Check for numerical overflow/underflow
    if (isNaN(d1) || isNaN(d2) || !isFinite(d1) || !isFinite(d2)) {
      return Math.max(S - K, 0);
    }

    const callPrice = S * normCDF(d1) - K * Math.exp(-r * T) * normCDF(d2);

    // Ensure result is valid
    if (isNaN(callPrice) || !isFinite(callPrice) || callPrice < 0) {
      return Math.max(S - K, 0);
    }

    return callPrice;
  } catch (error) {
    // Fallback to intrinsic value on any mathematical error
    return Math.max(S - K, 0);
  }
}

/**
 * Black-Scholes put option price calculation
 * @param {number} S - Current stock price
 * @param {number} K - Strike price
 * @param {number} T - Time to expiration (in years)
 * @param {number} r - Risk-free rate (as decimal, e.g., 0.05 for 5%)
 * @param {number} sigma - Volatility (as decimal, e.g., 0.3 for 30%)
 * @returns {number} Put option price
 */
export function blackScholesPut(S, K, T, r, sigma) {
  // Input validation to prevent mathematical errors
  if (T <= 0 || sigma <= 0 || S <= 0 || K <= 0) {
    return Math.max(K - S, 0); // Return intrinsic value
  }

  // Prevent extreme values that cause numerical issues
  if (S / K > 1000 || K / S > 1000) {
    // For extremely out-of-the-money options, return intrinsic value
    return Math.max(K - S, 0);
  }

  try {
    const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T));
    const d2 = d1 - sigma * Math.sqrt(T);

    // Check for numerical overflow/underflow
    if (isNaN(d1) || isNaN(d2) || !isFinite(d1) || !isFinite(d2)) {
      return Math.max(K - S, 0);
    }

    const putPrice = K * Math.exp(-r * T) * normCDF(-d2) - S * normCDF(-d1);

    // Ensure result is valid
    if (isNaN(putPrice) || !isFinite(putPrice) || putPrice < 0) {
      return Math.max(K - S, 0);
    }

    return putPrice;
  } catch (error) {
    // Fallback to intrinsic value on any mathematical error
    return Math.max(K - S, 0);
  }
}

/**
 * Calculate Black-Scholes option price based on option type
 * @param {number} S - Current stock price
 * @param {number} K - Strike price
 * @param {number} T - Time to expiration (in years)
 * @param {number} r - Risk-free rate (as decimal)
 * @param {number} sigma - Volatility (as decimal)
 * @param {string} optionType - 'call' or 'put'
 * @returns {number} Option price
 */
export function blackScholes(S, K, T, r, sigma, optionType) {
  if (optionType === 'call') {
    return blackScholesCall(S, K, T, r, sigma);
  } else if (optionType === 'put') {
    return blackScholesPut(S, K, T, r, sigma);
  } else {
    throw new Error(`Invalid option type: ${optionType}`);
  }
}

/**
 * Standard normal probability density function.
 */
function normPDF(x) {
  return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI);
}

/**
 * Black-Scholes Greeks, priced from the same inputs as the price functions above.
 *
 * Intended to be called with the exact (S, K, T, r, sigma) that produced a
 * displayed premium — pricing Greeks from anything else makes them disagree
 * with the number they sit next to.
 *
 * SCALING (must match what the UI labels claim):
 *   delta  raw, per $1 of underlying          (-1 .. 1)
 *   gamma  raw, delta per $1 of underlying
 *   theta  per CALENDAR DAY (annual / 365)
 *   vega   per 1 VOL POINT  (annual / 100), i.e. IV 28% -> 29%
 *   rho    per 1 RATE POINT (annual / 100), i.e. 5% -> 6%
 *   charm  delta per CALENDAR DAY of holding
 *
 * Charm is -d(delta)/d(tau): the delta the position loses per day held, so a
 * decaying OTM contract reads negative and an ITM one reads positive. With no
 * dividend yield, put and call charm are identical — put delta differs from
 * call delta by a constant, which vanishes under the time derivative.
 *
 * Returns all-null on any degenerate input (T, sigma, S or K non-positive, or a
 * numerical blow-up) so callers can hide the block rather than render NaN.
 *
 * @param {number} S - Underlying price
 * @param {number} K - Strike price
 * @param {number} T - Time to expiration in YEARS
 * @param {number} r - Risk-free rate (decimal, e.g. 0.05)
 * @param {number} sigma - Volatility (decimal, e.g. 0.3)
 * @param {string} optionType - 'call' or 'put'
 * @returns {{delta:number, gamma:number, theta:number, vega:number, rho:number, charm:number}|
 *           {delta:null, gamma:null, theta:null, vega:null, rho:null, charm:null}}
 */
export function blackScholesGreeks(S, K, T, r, sigma, optionType) {
  const NONE = { delta: null, gamma: null, theta: null, vega: null, rho: null, charm: null };

  // Same guards as the pricers: at or past expiry, or with a degenerate vol,
  // the Greeks are not defined (delta is a step function, gamma a spike).
  if (!(T > 0) || !(sigma > 0) || !(S > 0) || !(K > 0)) return NONE;
  if (S / K > 1000 || K / S > 1000) return NONE;

  try {
    const isCall = optionType === 'call';
    const sqrtT = Math.sqrt(T);
    const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * sqrtT);
    const d2 = d1 - sigma * sqrtT;

    if (!isFinite(d1) || !isFinite(d2)) return NONE;

    const pdf1 = normPDF(d1);
    const discount = Math.exp(-r * T);

    const delta = isCall ? normCDF(d1) : normCDF(d1) - 1;
    const gamma = pdf1 / (S * sigma * sqrtT);

    // Annualized theta, then per calendar day.
    const thetaAnnual = isCall
      ? (-(S * pdf1 * sigma) / (2 * sqrtT)) - r * K * discount * normCDF(d2)
      : (-(S * pdf1 * sigma) / (2 * sqrtT)) + r * K * discount * normCDF(-d2);
    const theta = thetaAnnual / 365;

    // Annualized vega/rho, then per single point of vol / rate.
    const vega = (S * pdf1 * sqrtT) / 100;
    const rhoAnnual = isCall
      ? K * T * discount * normCDF(d2)
      : -K * T * discount * normCDF(-d2);
    const rho = rhoAnnual / 100;

    // d(delta)/d(tau), annualized — identical for calls and puts at q = 0.
    const dDeltaDTau = pdf1 * (2 * r * T - d2 * sigma * sqrtT) / (2 * T * sigma * sqrtT);
    // Negate for "per day HELD" (tau shrinks as the holding period grows).
    const charm = -dDeltaDTau / 365;

    const out = { delta, gamma, theta, vega, rho, charm };
    for (const v of Object.values(out)) {
      if (typeof v !== 'number' || isNaN(v) || !isFinite(v)) return NONE;
    }
    return out;
  } catch (error) {
    return NONE;
  }
}

/**
 * Recalculate simulation values for a screener row
 * @param {Object} row - Screener result row
 * @param {number} daysToGain - Days to target (user input)
 * @param {number} underlyingGainPct - Expected stock movement % (user input)
 * @param {number} riskFreeRate - Risk-free rate (default 0.05)
 * @param {number|null} baselinePremium - Optional override for the % / P&L baseline.
 *   When null (screener default), the current market premium is used. The watchlist
 *   passes the user's avg cost when the global baseline toggle is in "avg_cost" mode.
 * @param {Object|null} intraday - Intraday-only override { timeToExpiryDays, tFloorYears }.
 *   When set, uses a fractional time-to-expiry (from getFractionalDaysToExpiry) and a
 *   finer near-expiry floor so sub-day holds price correctly. Null → original behavior
 *   (integer Days Until Expiration, 0.0001-yr floor) for every other caller.
 * @returns {Object} Updated row with recalculated simulation fields
 */
export function recalculateSimulation(row, daysToGain, underlyingGainPct, riskFreeRate = 0.05, baselinePremium = null, intraday = null) {
  // Extract necessary fields
  const underlyingPrice = row['Underlying Price'];
  const strike = row['Strike'];
  const daysUntilExp = row['Days Until Expiration'];
  const currentPremium = row['Current Premium'];
  const impliedVolatility = row['Implied Volatility'] / 100; // Convert from percentage
  const optionType = row['Option Type']?.toLowerCase();
  const positionType = row['Position Type']?.toLowerCase();

  // Baseline for % gain / P&L: fall back to current premium when missing or non-positive
  const effectiveBaseline = (Number.isFinite(baselinePremium) && baselinePremium > 0)
    ? baselinePremium
    : currentPremium;

  // Validate inputs
  if (!underlyingPrice || !strike || !daysUntilExp || !optionType) {
    console.warn('Missing required fields for recalculation:', row);
    return row;
  }

  // Calculate simulated values
  const simulatedStock = underlyingPrice * (1 + underlyingGainPct / 100);
  // Intraday uses a fractional time-to-expiry (sub-day) + finer floor; other
  // callers use the integer Days Until Expiration and the standard floor.
  const dteDays = intraday && Number.isFinite(intraday.timeToExpiryDays) ? intraday.timeToExpiryDays : daysUntilExp;
  const floorYears = intraday && intraday.tFloorYears ? intraday.tFloorYears : 0.0001;
  const timeToTarget = Math.max((dteDays - daysToGain) / 365, floorYears);

  // Calculate simulated premium using Black-Scholes
  const simulatedPremium = blackScholes(
    simulatedStock,
    strike,
    timeToTarget,
    riskFreeRate,
    impliedVolatility,
    optionType
  );

  // Calculate premium gain percentage - different for buy vs sell positions
  const isSell = positionType === 'sell';
  let premiumGain = 0;
  let returnOnCollateral = null;
  let collateralPerContract = null;

  if (effectiveBaseline > 0) {
    if (isSell) {
      // SELL: Profit when option premium DECREASES
      premiumGain = ((effectiveBaseline - simulatedPremium) / effectiveBaseline) * 100;

      // Calculate collateral and return on collateral for sells.
      // ROC is anchored to the premium *actually received* when the position
      // was opened — that's the current market premium at run time, not the
      // user's avg cost — so it does not honor the baseline toggle.
      if (optionType === 'put') {
        // Cash-secured put: need strike × 100 per contract
        collateralPerContract = strike * 100;
      } else {
        // Naked call: approximate margin (20% of underlying + premium)
        collateralPerContract = (underlyingPrice * 0.20 + currentPremium) * 100;
      }

      const premiumReceived = currentPremium * 100;
      returnOnCollateral = collateralPerContract > 0
        ? (premiumReceived / collateralPerContract) * 100
        : null;
    } else {
      // BUY: Profit when option premium INCREASES
      premiumGain = ((simulatedPremium - effectiveBaseline) / effectiveBaseline) * 100;
    }
  }

  // Helper function for annualized return calculation
  const calcAnnualized = (baseReturn, days) => {
    if (days === null || days === undefined || days <= 0 || baseReturn === null || baseReturn === undefined) {
      return null;
    }
    try {
      return (Math.pow(1 + baseReturn / 100, 365 / days) - 1) * 100;
    } catch (e) {
      return null;
    }
  };

  // Determine base return based on position type
  const baseReturn = isSell ? returnOnCollateral : premiumGain;

  // Calculate both annualized versions
  const annReturnTarget = calcAnnualized(baseReturn, daysToGain);
  const annReturnExpiry = calcAnnualized(baseReturn, daysUntilExp);

  // Calculate equity values if allocation exists
  const allocation = row['Allocated Equity'];
  const simulatedEquity = allocation
    ? allocation * (1 + premiumGain / 100)
    : null;

  // Build updated row
  let updatedRow = {
    ...row,
    'Days to Gain': daysToGain,
    'Underlying Gain %': underlyingGainPct,
    'Simulated Underlying': simulatedStock,
    'Simulated Premium': simulatedPremium,
    'Premium % Gain': premiumGain,
    'Ann. Return (Target)': annReturnTarget !== null ? annReturnTarget : 'NA',
    'Ann. Return (Expiry)': annReturnExpiry !== null ? annReturnExpiry : 'NA',
    'Simulated Equity': simulatedEquity
  };

  // Update sell-specific fields if this is a sell position
  if (isSell && currentPremium > 0) {
    const premiumReceived = currentPremium * 100;

    updatedRow['Collateral Required'] = collateralPerContract;
    updatedRow['Return on Collateral %'] = returnOnCollateral;
    updatedRow['Max Profit'] = premiumReceived;

    if (optionType === 'put') {
      updatedRow['Max Loss'] = (strike - currentPremium) * 100;
    } else {
      updatedRow['Max Loss'] = 'Unlimited';
    }

    // Simulated P&L for sell positions — uses the toggle-resolved baseline so
    // "avg cost" mode reflects the user's realized entry, not today's market.
    const numContracts = row['Number of Contracts'] || 1;
    updatedRow['Simulated P&L'] = (effectiveBaseline - simulatedPremium) * numContracts * 100;
  }

  return updatedRow;
}

/**
 * Batch recalculate simulations for multiple rows
 * @param {Array} rows - Array of screener result rows
 * @param {number} daysToGain - Days to target (user input)
 * @param {number} underlyingGainPct - Expected stock movement % (user input)
 * @returns {Array} Array of updated rows
 */
export function batchRecalculateSimulations(rows, daysToGain, underlyingGainPct) {
  return rows.map(row => recalculateSimulation(row, daysToGain, underlyingGainPct));
}

/**
 * Get the maximum days to target from a set of results
 * @param {Array} results - Array of screener results
 * @returns {number} Minimum days until expiration across all results
 */
export function getMaxDaysToTarget(results) {
  if (!results || results.length === 0) return 365;

  const expirationDays = results
    .map(row => row['Days Until Expiration'])
    .filter(days => days && days > 0);

  if (expirationDays.length === 0) return 365;

  return Math.min(...expirationDays);
}

/**
 * Find the underlying price at which P&L = 0 using binary search over Black-Scholes.
 * @param {number} strike - Strike price
 * @param {number} currentPremium - Premium paid/received at entry
 * @param {number} T - Time remaining at exit (in years), e.g., (DTE - daysToExit) / 365
 * @param {number} iv - Implied volatility as decimal (e.g., 0.30)
 * @param {string} optionType - 'call' or 'put'
 * @param {boolean} isSell - true for sell/write positions, false for buy
 * @param {number} riskFreeRate - Risk-free rate (default 0.05)
 * @returns {number|null} Breakeven underlying price, or null if inputs are invalid
 */
export function findBreakevenPrice(strike, currentPremium, T, iv, optionType, isSell, riskFreeRate = 0.05) {
  if (!strike || !currentPremium || !iv || iv <= 0 || !optionType) return null;

  const safeT = Math.max(T, 0.0001);
  let low = strike * 0.01;
  let high = strike * 12;

  for (let i = 0; i < 50; i++) {
    const mid = (low + high) / 2;
    const premium = blackScholes(mid, strike, safeT, riskFreeRate, iv, optionType);
    const pnl = isSell ? (currentPremium - premium) : (premium - currentPremium);

    if (Math.abs(pnl) < 0.001) return mid;

    if (optionType === 'call') {
      isSell ? (pnl > 0 ? (low = mid) : (high = mid)) : (pnl < 0 ? (low = mid) : (high = mid));
    } else {
      isSell ? (pnl > 0 ? (high = mid) : (low = mid)) : (pnl < 0 ? (high = mid) : (low = mid));
    }
  }
  return (low + high) / 2;
}